import React from 'react';
import moment from "moment-timezone";

import { findAllNominationSummaries } from '../../queries/Queries';
import QueryDataTable from '../../components/QueryDataTable/QueryDataTable';
import { getPortalTimezone } from "../../utils/dstUtils";

/**
 * @fileoverview NominationSummaryView - A comprehensive data table view for managing nomination summaries
 *
 * This component provides a feature-rich interface for viewing and filtering nomination summary data
 * with advanced table capabilities including sorting, pagination, and multiple filter types.
 *
 * @author Nikola Ignatov
 * @since 2025-09-25
 */

/**
 * Type enumeration for nomination types
 * @enum {string}
 */
const NominationType = {
  /** Initial nomination submission */
  NOMINATION: 'NOMINATION',
  /** Modified/updated nomination submission */
  RENOMINATION: 'RENOMINATION',
};

/**
 * Status enumeration for nomination summaries
 * @enum {string}
 */
const NominationStatus = {
  /** Nomination is waiting to be processed */
  PENDING: 'PENDING',
  /** Successfully sent to Trade Cube system */
  SENT_TO_TRADE_CUBE: 'SENT_TO_TRADE_CUBE',
  /** Failed to send to Trade Cube system */
  FAILED_TO_SEND_TO_TRADE_CUBE: 'FAILED_TO_SEND_TO_TRADE_CUBE',
};

/**
 * Display mapping for status values to provide user-friendly labels
 * Maps internal status enum values to readable display text
 * @type {Object.<string, string>}
 */
const StatusDisplayMap = {
  [NominationStatus.PENDING]: 'Pending',
  [NominationStatus.SENT_TO_TRADE_CUBE]: 'Sent',
  [NominationStatus.FAILED_TO_SEND_TO_TRADE_CUBE]: 'Failed',
};

/**
 * NominationSummaryView Component
 *
 * A comprehensive data table view for displaying and managing nomination summary records.
 * Provides advanced filtering, sorting, and pagination capabilities with a responsive design.
 *
 * Features:
 * - Multi-column sortable table with formatted data display
 * - Date-based filtering with single date picker
 * - Dropdown filters for nomination type and status
 * - Scrollable content areas for long text fields (data and process results)
 * - Color-coded status indicators (green for sent, red for failed)
 * - Responsive column layout with fixed widths for optimal viewing
 * - GraphQL-powered data fetching with pagination
 *
 * Data Sources:
 * - Uses `findAllNominationSummaries` GraphQL query
 * - Supports server-side filtering through buildWhereClause function
 *
 * Column Details:
 * - Settlement Day: Date formatted as DD/MM/YYYY, sortable
 * - Submitted Date: UTC date converted to Europe/London timezone with time
 * - Process Result: Scrollable container for long exception strings
 * - Data: JSON formatted data in scrollable pre-formatted container
 * - Type: Nomination type (NOMINATION/RENOMINATION), sortable
 * - Buy/Sell: Transaction direction, sortable
 * - Trade Cube Retries: Retry count, sortable
 * - Status: Color-coded status with enum-based display mapping
 *
 * Filter Options:
 * - Settlement Day: Single date picker for filtering by settlement date
 * - Type Filter: Dropdown to filter by nomination type
 * - Status Filter: Dropdown to filter by processing status
 *
 * @component
 * @returns {React.ReactElement} Rendered NominationSummaryView component
 *
 * @example
 * // Basic usage
 * <NominationSummaryView />
 *
 * @since 2025-09-25
 */

const NominationSummaryView = () => {
  /**
   * Column configuration for the data table
   * Defines how each field should be displayed, formatted, and styled
   * @type {Array<Object>}
   */
  const columns = [
    {
      dataField: 'settlementDay',
      text: 'Settlement Day',
      sort: true,
      formatter: (cell) => moment(cell).format('DD/MM/YYYY'),
    },
    {
      dataField: 'maxVersionDate',
      text: 'Submitted Date',
      sort: true,
      formatter: (cell) => moment.utc(cell).tz(getPortalTimezone()).format('DD/MM/YYYY HH:mm'),
    },
    {
      dataField: 'processResult',
      text: 'Process Result',
      headerStyle: { width: '200px' },
      formatter: (cell) => {
        if (!cell) return null;

        return (
          <div
            style={{
              maxWidth: '200px',
              maxHeight: '100px',
              overflowY: 'auto',
              overflowX: 'hidden',
              wordWrap: 'break-word',
              whiteSpace: 'pre-wrap',
              fontSize: '12px',
              lineHeight: '1.2',
              padding: '4px',
              border: cell.length > 100 ? '1px solid #ddd' : 'none',
              borderRadius: '4px',
              backgroundColor: cell.length > 100 ? '#f8f9fa' : 'transparent',
            }}
            title={cell} // Show full text on hover
          >
            {cell}
          </div>
        );
      },
    },
    {
      dataField: 'data',
      text: 'Data',
      formatter: (cell) => {
        try {
          const data = JSON.parse(cell);
          return (
            <pre
              style={{
                whiteSpace: 'pre',
                overflowX: 'auto',
                overflowY: 'auto',
                maxWidth: '100%',
                maxHeight: '150px',
                textAlign: 'left',
                margin: 0,
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: '#f8f9fa',
              }}
            >
              {JSON.stringify(data, null, 2)}
            </pre>
          );
        } catch (e) {
          return cell;
        }
      },
    },
    {
      dataField: 'type',
      text: 'Type',
      sort: true,
    },
    {
      dataField: 'buySell',
      text: 'Buy/Sell',
      sort: true,
    },
    {
      dataField: 'retryTradeCubeCount',
      text: 'Trade Cube Retries',
      sort: true,
    },
    {
      dataField: 'status',
      text: 'Status',
      sort: true,
      formatter: (cell) => {
        const displayText = StatusDisplayMap[cell] || cell;
        let color = 'inherit';

        if (cell === NominationStatus.SENT_TO_TRADE_CUBE) {
          color = 'green';
        } else if (cell === NominationStatus.FAILED_TO_SEND_TO_TRADE_CUBE) {
          color = 'red';
        }

        return <span style={{ color, fontWeight: 'bold' }}>{displayText}</span>;
      },
    },
  ];

  /**
   * Builds the GraphQL where clause based on active filter values
   * Constructs query conditions for server-side filtering
   *
   * @param {Object} filters - Current filter values from the UI
   * @param {moment.Moment} [filters.selectedSettlementDay] - Selected settlement date
   * @param {string} [filters.typeFilter] - Selected nomination type filter
   * @param {string} [filters.statusFilter] - Selected status filter
   * @returns {string} Formatted where clause string for GraphQL query
   *
   * @example
   * // Returns: "settlementDay == '2023-09-25' and type == 'NOMINATION' and status == 'SENT_TO_TRADE_CUBE'"
   * buildWhereClause({
   *   selectedSettlementDay: moment('2023-09-25'),
   *   typeFilter: 'NOMINATION',
   *   statusFilter: 'SENT_TO_TRADE_CUBE'
   * });
   */
  const buildWhereClause = (filters) => {
    const conditions = [];

    if (filters.selectedSettlementDay) {
      conditions.push(`settlementDay == '${filters.selectedSettlementDay.format('YYYY-MM-DD')}'`);
    }

    if (filters.typeFilter) {
      conditions.push(`type == '${filters.typeFilter}'`);
    }

    if (filters.statusFilter) {
      conditions.push(`status == '${filters.statusFilter}'`);
    }

    return conditions.length > 0 ? conditions.join(' and ') : '';
  };

  return (
    <QueryDataTable
      title='Nomination Summary'
      query={findAllNominationSummaries}
      columns={columns}
      keyField='maxVersionDate'
      defaultSort={{ field: 'created', order: 'desc' }}
      pageSizes={[10, 20, 50]}
      filters={[
        {
          type: 'singleDate',
          name: 'settlementDay',
          dateField: 'selectedSettlementDay',
          placeholder: 'Filter by Settlement Day',
          colSize: 3,
        },
        {
          type: 'dropdown',
          name: 'typeFilter',
          placeholder: 'All Types',
          colSize: 3,
          options: [
            { value: NominationType.NOMINATION, label: 'Nomination' },
            { value: NominationType.RENOMINATION, label: 'Renomination' },
          ],
          defaultValues: '',
        },
        {
          type: 'dropdown',
          name: 'statusFilter',
          placeholder: 'All Statuses',
          colSize: 3,
          options: [
            { value: NominationStatus.SENT_TO_TRADE_CUBE, label: 'Sent' },
            { value: NominationStatus.FAILED_TO_SEND_TO_TRADE_CUBE, label: 'Failed' },
            { value: NominationStatus.PENDING, label: 'Pending' },
          ],
          defaultValues: '',
        },
      ]}
      buildWhereClause={buildWhereClause}
      queryName='findAllNominationSummaries'
    />
  );
};

export default NominationSummaryView;
